
import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  CheckSquare,
  Calendar,
  DollarSign,
  Ticket,
  Settings,
  ChevronRight,
  UserSearch,
  X,
  FileText
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface ScoutSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const ScoutSidebar: React.FC<ScoutSidebarProps> = ({ isOpen = true, onClose }) => {
  const location = useLocation();
  
  const menuItems = [
    {
      title: "Dashboard",
      href: "/scout-dashboard",
      icon: <LayoutDashboard className="h-5 w-5" />,
    },
    {
      title: "My Athletes",
      href: "/scout-dashboard/athletes",
      icon: <Users className="h-5 w-5" />,
    },
    {
      title: "Tasks",
      href: "/scout-dashboard/tasks",
      icon: <CheckSquare className="h-5 w-5" />,
    },
    {
      title: "Calendar",
      href: "/scout-dashboard/calendar",
      icon: <Calendar className="h-5 w-5" />,
    },
    {
      title: "Finance",
      href: "/scout-dashboard/finance",
      icon: <DollarSign className="h-5 w-5" />,
    },
    {
      title: "Coupons",
      href: "/scout-dashboard/coupons",
      icon: <Ticket className="h-5 w-5" />,
    },
    {
      title: "W-9 Form",
      href: "/scout-dashboard/w9",
      icon: <FileText className="h-5 w-5" />,
    },
    {
      title: "Settings",
      href: "/scout-dashboard/settings",
      icon: <Settings className="h-5 w-5" />,
    }
  ];

  // Function to check if the navItem is active
  const isActive = (path: string) => {
    if (path === '/scout-dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLinkClick = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={cn(
        "bg-slate-900 dark:bg-gray-950 border-r border-slate-800 dark:border-gray-800 shadow-lg h-full transition-all duration-300 z-50",
        // Mobile: Fixed position, slide in/out
        "md:relative md:translate-x-0",
        "fixed left-0 top-0 w-80 max-w-[80vw]",
        isOpen ? "translate-x-0" : "-translate-x-full",
        // Desktop: Always visible, normal width  
        "md:w-64"
      )}>
        <div className="flex flex-col h-full">
          {/* Header with close button on mobile */}
          <div className="p-4 md:p-5 border-b border-slate-800 dark:border-gray-800">
            <div className="flex items-center justify-between">
              <div className="text-lg md:text-xl font-bold text-white flex items-center">
                <UserSearch className="h-5 w-5 md:h-6 md:w-6 mr-2 text-secondary flex-shrink-0" />
                <span className="font-heading text-secondary truncate">Scout Dashboard</span>
              </div>
              {/* Close button - only visible on mobile */}
              {onClose && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="md:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800"
                >
                  <X className="h-5 w-5" />
                </Button>
              )}
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-3">
              {menuItems.map((item) => (
                <li key={item.title}>
                  <Link
                    to={item.href}
                    onClick={handleLinkClick}
                    className={cn(
                      "flex items-center py-3 md:py-2 px-3 rounded-md text-sm transition-colors touch-manipulation",
                      "min-h-[44px] md:min-h-0", // Touch-friendly height on mobile
                      isActive(item.href)
                        ? "bg-secondary/20 text-secondary-foreground hover:bg-secondary/30"
                        : "text-slate-300 dark:text-gray-300 hover:bg-slate-800/60 dark:hover:bg-gray-800/60"
                    )}
                  >
                    <div className="flex items-center justify-start w-full">
                      {item.icon}
                      <span className="ml-3 font-medium">{item.title}</span>
                      {isActive(item.href) && (
                        <ChevronRight className="h-4 w-4 ml-auto text-secondary" />
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          
          {/* Footer */}
          <div className="p-4 border-t border-slate-800 dark:border-gray-800 bg-slate-800/30">
            <div className="text-xs text-slate-400">
              Scout Portal v1.0
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default ScoutSidebar;
